import { useAuthStore } from '@/stores/authStore'

export type TemperatureUnit = 'celsius' | 'fahrenheit'
export type SpeedUnit = 'kph' | 'mph'

/**
 * Get the user's unit preferences from the auth store.
 */
export function getUserUnits() {
  const user = useAuthStore.getState().user
  return {
    temperature: (user?.preferences?.unitsTemperature ?? 'celsius') as TemperatureUnit,
    speed: (user?.preferences?.unitsSpeed ?? 'kph') as SpeedUnit,
  }
}

/**
 * React hook to get the user's unit preferences reactively.
 */
export function useUserUnits() {
  const user = useAuthStore(state => state.user)
  return {
    temperature: (user?.preferences?.unitsTemperature ?? 'celsius') as TemperatureUnit,
    speed: (user?.preferences?.unitsSpeed ?? 'kph') as SpeedUnit,
  }
}

/**
 * Convert Celsius to the user's preferred temperature unit.
 */
export function convertTemp(celsius: number, unit: TemperatureUnit): number {
  if (unit === 'fahrenheit') return celsius * 9 / 5 + 32
  return celsius
}

/**
 * Format a temperature value with unit label.
 */
export function formatTemp(celsius: number, unit: TemperatureUnit, decimals = 1): string {
  const value = convertTemp(celsius, unit)
  const symbol = unit === 'fahrenheit' ? '°F' : '°C'
  return `${value.toFixed(decimals)}${symbol}`
}

/**
 * Get temperature unit label.
 */
export function tempLabel(unit: TemperatureUnit): string {
  return unit === 'fahrenheit' ? '°F' : '°C'
}

/**
 * Convert m/s to the user's preferred speed unit.
 * The API/measurements store wind speed in m/s.
 */
export function convertSpeed(ms: number, unit: SpeedUnit): number {
  if (unit === 'mph') return ms * 2.23694
  // kph
  return ms * 3.6
}

/**
 * Format a wind speed value with unit label.
 */
export function formatSpeed(ms: number, unit: SpeedUnit, decimals = 1): string {
  const value = convertSpeed(ms, unit)
  const label = unit === 'mph' ? 'mph' : 'km/h'
  return `${value.toFixed(decimals)} ${label}`
}

/**
 * Get speed unit label.
 */
export function speedLabel(unit: SpeedUnit): string {
  return unit === 'mph' ? 'mph' : 'km/h'
}

/**
 * Map user speed preference to WindRoseChart's expected format.
 */
export function speedUnitForWindRose(unit: SpeedUnit): string {
  return unit === 'mph' ? 'mph' : 'kph'
}
